import React, { useEffect, useMemo, useState } from 'react';
import AuthService from "../Services/AuthService";

const MONTH_SHORT_NAMES = [
  "Ene",
  "Feb",
  "Mar",
  "Abr",
  "May",
  "Jun",
  "Jul",
  "Ago",
  "Sep",
  "Oct",
  "Nov",
  "Dic"
];


const getDateLabel = (monthNumber, year) => {
  const parsedMonth = Number(monthNumber);
  if (parsedMonth >= 1 && parsedMonth <= 12) {
    return `${MONTH_SHORT_NAMES[parsedMonth - 1]}-${year}`;
  }
  return `${year}`;
};

const getDownloadUrl = (filePath) => {
  if (!filePath) {
    return "";
  }

  if (filePath.startsWith("http://") || filePath.startsWith("https://")) {
    return filePath;
  }

  if (filePath.startsWith("s3://")) {
    const s3Path = filePath.replace("s3://", "");
    const firstSlash = s3Path.indexOf("/");
    if (firstSlash === -1) {
      return "";
    }

    const bucket = s3Path.substring(0, firstSlash);
    const key = s3Path.substring(firstSlash + 1);
    const encodedKey = key.split("/").map(encodeURIComponent).join("/");
    return `https://${bucket}.s3.amazonaws.com/${encodedKey}`;
  }

  return filePath;
};

export default function Compensaciones(props) {
  const [currentUser] = useState(props.user);
  const [reports, setReports] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    const loadReports = async () => {
      if (!currentUser || !currentUser.idu) {
        setIsLoading(false);
        setErrorMsg("No fue posible identificar al usuario para consultar compensaciones.");
        return;
      }

      try {
        setIsLoading(true);
        setErrorMsg("");

        const response = await AuthService.getCompensacionesAPI(currentUser.idu);
        const payload = response && response.data ? response.data : {};
        const dataReports = Array.isArray(payload.reports) ? payload.reports : [];

        setReports(dataReports);

        if (!payload.exito) {
          setErrorMsg("No fue posible obtener los reportes de compensaciones.");
        }
      } catch (error) {
        console.log(error);
        if (error && error.response && error.response.status === 404) {
          setErrorMsg("No se encontraron reportes de compensaciones");
        } else {
          setErrorMsg("Ocurrió un error al consultar tus compensaciones. Intenta nuevamente.");
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadReports();
  }, [currentUser]);

  const sortedReports = useMemo(() => {
    return [...reports].sort((a, b) => {
      const yearSort = Number(b.Year) - Number(a.Year);
      if (yearSort !== 0) {
        return yearSort;
      }
      return Number(b.Month) - Number(a.Month);
    });
  }, [reports]);

  const handleDownload = (filePath) => {
    const targetUrl = getDownloadUrl(filePath);
    if (!targetUrl) {
      return;
    }

    window.open(targetUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <div style={{ maxWidth: 900, margin: "0 auto", padding: "8px 10px 24px 10px" }}>
      <div style={{
        padding: "8px 2px",
        marginBottom: 12
      }}>
        <h3 style={{
          margin: 0,
          fontSize: 22,
          fontWeight: 700,
          color: "var(--Vorwerk-Green)",
          textAlign: "center",
          lineHeight: 1.2,
          letterSpacing: "0.3px"
        }}>
          Mis compensaciones
        </h3>
        <p style={{
          margin: "12px 0 0 0",
          fontSize: 14,
          fontWeight: 500,
          textAlign: "center",
          lineHeight: 1.2
        }}>
          Tu resumen de compensaciones estará
          <br />
          disponible aproximadamente en la semana
          <br />
          siguiente al cierre de mes comercial
        </p>
      </div>

      {isLoading && (
        <div style={{ padding: "10px 4px", fontSize: 16, fontWeight: 600 }}>
          Cargando compensaciones...
        </div>
      )}

      {!isLoading && errorMsg && (
        <div style={{
          marginBottom: 14,
          padding: "12px 14px",
          border: "1px solid #d32f2f",
          backgroundColor: "#fdeaea",
          color: "#9a1f1f",
          fontSize: 15,
          fontWeight: 600
        }}>
          {errorMsg}
        </div>
      )}

      {!isLoading && sortedReports.length === 0 && !errorMsg && (
        <div style={{
          marginBottom: 14,
          padding: "12px 14px",
          border: "1px solid #c6c6c6",
          backgroundColor: "#f7f7f7",
          color: "#4a4a4a",
          fontSize: 15,
          fontWeight: 600
        }}>
          No hay compensaciones disponibles por el momento.
        </div>
      )}

      {!isLoading && sortedReports.length > 0 && (
        <div
          style={{
            borderTop: "1px solid #b8b8b8",
            borderBottom: "1px solid #b8b8b8",
            background: "#efefef"
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "2fr 1fr 1.4fr",
              alignItems: "center",
              gap: 8,
              padding: "10px 14px",
              borderBottom: "1px solid #b8b8b8",
              fontSize: 14,
              fontWeight: 700,
              color: "var(--Vorwerk-Green)"
            }}
          >
            <div style={{ textAlign: "left" }}>Fecha</div>
            <div style={{ textAlign: "center" }}>Doc</div>
            <div style={{ textAlign: "left" }}>ID</div>
          </div>

          {sortedReports.map((report) => (
            <div
              key={report.id || report._id || `${report.Year}-${report.Month}-${report.InternalID}`}
              style={{
                display: "grid",
                gridTemplateColumns: "2fr 1fr 1.4fr",
                alignItems: "center",
                gap: 8,
                padding: "14px",
                borderBottom: "1px solid #b8b8b8",
                minHeight: 72
              }}
            >
              <div style={{
                fontSize: 18,
                fontWeight: 700,
                lineHeight: 1,
                textAlign: "left"
              }}>
                {getDateLabel(report.Month, report.Year)}
              </div>

              <div style={{ display: "flex", justifyContent: "center" }}>
                <button
                  type="button"
                  onClick={() => handleDownload(report.FilePath)}
                  style={{
                    cursor: "pointer",
                    border: "none",
                    borderRadius: 8,
                    background: "#00AC46",
                    color: "#fff",
                    fontSize: 14,
                    fontWeight: 700,
                    padding: "7px 5px",
                    minWidth: 44,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                  }}
                  aria-label="Descargar"
                >
                  <img src="/assets/img/icons/descargar_white_23.png" alt="Descargar" style={{ width: 25, height: 25 }} />
                </button>
              </div>

              <div style={{
                fontSize: 16,
                fontWeight: 700,
                lineHeight: 1,
                textAlign: "left"
              }}>
                {report.InternalID || report.id || report._id || "-"}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
